<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import type { Trip } from "../../lib/types";
  import TripCard from "../../components/card/TripCard.svelte";
  import Modal from "../../components/overlay/Modal.svelte";
  import EmptyState from "../../components/feedback/EmptyState.svelte";
  import Layout from "../../components/layout/Layout.svelte";
  import DestinationPicker from "../../components/input/DestinationPicker.svelte";
  import { tripsStore, addNotification } from "../../lib/stores";
  import { tripsApi } from "../../lib/api";
  import { formatCurrency } from "../../lib/utils/format";
  import Button from "../../components/input/Button.svelte";

  let { trips = [], meta = null, user = null }: {
    trips?: Trip[];
    meta?: { page: number; total: number; totalPages: number } | null;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  // Local state - synced with props but reactive for optimistic updates
  let localTrips: Trip[] = $state([]);
  let tripsHydrated = false;

  let showCreateModal = $state(false);
  let showDestinationPicker = $state(false);
  let loading = $state(false);

  // Sync props changes to local state
  $effect(() => {
    if (tripsHydrated) return;
    localTrips = [...trips];
    tripsHydrated = true;
  });

  // Form data
  let newTrip = $state({
    name: "",
    destination: "",
    startDate: "",
    endDate: "",
    budgetCents: 0,
    budgetCurrency: "KRW",
    description: "",
  });

  async function createTrip() {
    loading = true;
    try {
      const response = await tripsApi.create({
        name: newTrip.name,
        destination: newTrip.destination,
        startDate: newTrip.startDate,
        endDate: newTrip.endDate,
        budgetCents: newTrip.budgetCents * 100,
        budgetCurrency: newTrip.budgetCurrency,
        description: newTrip.description,
      });

      if (response.success) {
        // Optimistic update: add trip to local state immediately
        localTrips = [...localTrips, response.data];
        tripsStore.addTrip(response.data);
        addNotification("success", "여행이 생성되었습니다!");
        showCreateModal = false;
        resetForm();
        // Don't reload - local state is already updated and notification will show
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        addNotification("error", error.response.data.errors[0]?.message || "여행 생성에 실패했습니다.");
      } else {
        addNotification("error", "여행 생성에 실패했습니다.");
      }
    } finally {
      loading = false;
    }
  }

  function resetForm() {
    newTrip = {
      name: "",
      destination: "",
      startDate: "",
      endDate: "",
      budgetCents: 0,
      budgetCurrency: "KRW",
      description: "",
    };
  }

  // Popular destinations
  const popularDestinations = [
    { name: "도쿄", country: "일본", flag: "🇯🇵" },
    { name: "방콕", country: "태국", flag: "🇹🇭" },
    { name: "파리", country: "프랑스", flag: "🇫🇷" },
    { name: "싱가포르", country: "싱가포르", flag: "🇸🇬" },
  ];

  // 목적지 이모지 매핑
  const destinationEmojis: Record<string, string> = {
    // 국내
    '서울': '🏙️', '제주': '🏝️', '부산': '🌊', '강릉': '⛱️',
    '경주': '🏛️', '전주': '🏘️', '여수': '🌅', '속초': '🏔️',
    '춘천': '🍂', '양양': '🏄', '가평': '🌲', '대구': '🌸',
    '대전': '🚄', '광주': '🎨', '인천': '✈️', '울산': '🐋',
    // 해외
    '도쿄': '🗼', '오사카': '🏯', '교토': '⛩️', '후쿠오카': '🍜',
    '삿포로': '❄️', '오키나와': '🏖️', '나고야': '🏰',
    '방콕': '🛕', '치앙마이': '🐘', '푸켓': '🏝️',
    '다낭': '🌴', '호치민': '🏍️', '하노이': '🍲', '나트랑': '🏊',
    '발리': '🌺', '싱가포르': '🦁',
    '홍콩': '🌃', '마카오': '🎰', '타이베이': '🧋', '상하이': '🌆',
    '뉴욕': '🗽', 'LA': '🎬', '하와이': '🌈',
    '파리': '🗼', '런던': '🎡', '로마': '🏛️', '바르셀로나': '⚽',
  };

  function getDestinationEmoji(destination: string): string {
    return destinationEmojis[destination] || '📍';
  }

  function handleDestinationSelect(name: string) {
    newTrip.destination = name;
  }
</script>

<Layout {user}>
  <div class="min-h-screen" style="background: var(--bg);">
    <!-- Header -->
    <header style="border-bottom: 1px solid var(--border);">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold" style="color: var(--text-primary);">내 여행</h1>
            <p class="mt-1 text-sm" style="color: var(--text-secondary);">
              총 {meta?.total || localTrips.length}개의 여행
            </p>
          </div>
          <Button variant="primary" class="hidden sm:flex" onclick={() => (showCreateModal = true)}>새 여행 만들기</Button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {#if localTrips.length === 0}
        <div class="card">
          <EmptyState
            icon="trip"
            title="아직 여행이 없습니다"
            description="첫 번째 여행을 만들어보세요!"
          >
            <Button variant="primary" onclick={() => (showCreateModal = true)}>여행 만들기</Button>
          </EmptyState>
        </div>

        <!-- Popular Destinations -->
        <div class="mt-10">
          <h2 class="text-base font-semibold mb-4" style="color: var(--text-primary);">인기 여행지</h2>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            {#each popularDestinations as dest}
              <button
                onclick={() => {
                  newTrip.destination = dest.name;
                  showCreateModal = true;
                }}
                class="card card-hover p-4 text-left cursor-pointer"
              >
                <span class="text-2xl">{getDestinationEmoji(dest.name)}</span>
                <p class="font-medium mt-2 text-sm" style="color: var(--text-primary);">{dest.name}</p>
                <p class="text-xs mt-0.5" style="color: var(--text-muted);">{dest.country}</p>
              </button>
            {/each}
          </div>
        </div>
      {:else}
        <!-- Trip Stats -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div class="card p-4">
            <p class="text-xs" style="color: var(--text-muted);">전체 여행</p>
            <p class="text-2xl font-bold mt-1" style="color: var(--text-primary);">{trips.length}</p>
          </div>
          <div class="card p-4">
            <p class="text-xs" style="color: var(--text-muted);">진행중</p>
            <p class="text-2xl font-bold mt-1" style="color: #34d399;">
              {trips.filter(t => t.status === "active").length}
            </p>
          </div>
          <div class="card p-4">
            <p class="text-xs" style="color: var(--text-muted);">계획중</p>
            <p class="text-2xl font-bold mt-1" style="color: #f59e0b;">
              {trips.filter(t => t.status === "planning").length}
            </p>
          </div>
          <div class="card p-4">
            <p class="text-xs" style="color: var(--text-muted);">완료</p>
            <p class="text-2xl font-bold mt-1" style="color: var(--accent);">
              {trips.filter(t => t.status === "completed").length}
            </p>
          </div>
        </div>

        <!-- Trip Grid -->
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {#each localTrips as trip (trip.id)}
            <TripCard {trip} />
          {/each}
        </div>
      {/if}
    </main>

    <!-- Create Trip Modal -->
    <Modal
      show={showCreateModal}
      title="새 여행 만들기"
      size="md"
      onClose={() => {
        showCreateModal = false;
        resetForm();
      }}
    >
      <form onsubmit={(e) => { e.preventDefault(); createTrip(); }} class="space-y-4">
        <div>
          <label for="name" class="label">여행 이름</label>
          <input id="name" type="text" bind:value={newTrip.name} class="input" placeholder="후쿠오카 여행" required />
        </div>

        <div>
          <label for="destination" class="label">목적지</label>
          <button
            type="button"
            onclick={() => (showDestinationPicker = true)}
            class="input w-full flex items-center justify-between cursor-pointer"
          >
            {#if newTrip.destination}
              <span class="flex items-center gap-2">
                <span>{getDestinationEmoji(newTrip.destination)}</span>
                <span style="color: var(--text-primary);">{newTrip.destination}</span>
              </span>
            {:else}
              <span style="color: var(--text-muted);">목적지를 선택하세요</span>
            {/if}
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="startDate" class="label">시작일</label>
            <input id="startDate" type="date" bind:value={newTrip.startDate} class="input" required />
          </div>
          <div>
            <label for="endDate" class="label">종료일</label>
            <input id="endDate" type="date" bind:value={newTrip.endDate} class="input" min={newTrip.startDate} required />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="budget" class="label">예산</label>
            <input id="budget" type="number" bind:value={newTrip.budgetCents} class="input" placeholder="1000000" min="0" required />
          </div>
          <div>
            <label for="currency" class="label">통화</label>
            <select id="currency" bind:value={newTrip.budgetCurrency} class="input">
              <option value="KRW">KRW (원)</option>
              <option value="USD">USD ($)</option>
              <option value="JPY">JPY (¥)</option>
              <option value="EUR">EUR (€)</option>
            </select>
          </div>
        </div>

        <div>
          <label for="description" class="label">설명 (선택)</label>
          <textarea id="description" bind:value={newTrip.description} class="input" rows="2" placeholder="여행에 대한 간단한 설명..."></textarea>
        </div>

        <div class="flex justify-end space-x-3 pt-2">
          <Button type="button" variant="secondary" onclick={() => { showCreateModal = false; resetForm(); }}>취소</Button>
          <Button type="submit" variant="primary" loading={loading}>생성하기</Button>
        </div>
      </form>
    </Modal>

    <!-- Destination Picker Modal -->
    <DestinationPicker
      show={showDestinationPicker}
      selectedDestination={newTrip.destination}
      onSelect={handleDestinationSelect}
      onClose={() => showDestinationPicker = false}
    />
  </div>
</Layout>
